import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useDecidir, useRespuesta } from './api';
import { fechaHora } from './piezas';

/**
 * RevisarRespuesta — lo que alguien diligenció, y qué se hace con ello.
 *
 * ## Lo que cambia
 *
 * - **Nada de lo que llega se pinta como HTML.** El vanilla interpolaba
 *   `d.who`, `x.current` y `x.proposed` sin escapar en un `innerHTML`, y ese
 *   texto lo escribe **quien responde la invitación** — que puede ser un correo
 *   externo, porque el formulario de personas nuevas existe justo para eso. Lo
 *   abría Bienestar. Era la peor de las dieciséis interpolaciones sin escapar
 *   de la pantalla.
 * - **«Devolver» dice qué falta.** Ya exigía la nota, pero el aviso salía como
 *   un mensaje efímero; aquí el error se queda junto al campo.
 * - **Las fechas se ven en la hora de quien mira.**
 */
export function RevisarRespuesta({ id, onCerrar }: { id: number; onCerrar: () => void }) {
  const consulta = useRespuesta(id);
  const decidir = useDecidir();
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [clave, setClave] = useState<string | null>(null);

  const d = consulta.data;
  const campos = d?.fields ?? [];
  const cambios = campos.filter((x) => x.changed);

  function decide(decision: 'aprobar' | 'devolver' | 'rechazar') {
    if (decision === 'devolver' && !nota.trim()) {
      setError('Escribe qué debe corregirse: es lo único que verá quien la recibe.');
      return;
    }
    decidir.mutate(
      { id, decision, note: nota.trim() || null },
      {
        onSuccess: (r) => {
          if (r.temp_password) {
            // La cuenta se creó: la contraseña temporal se enseña una vez y
            // también va al correo de la persona.
            setClave(r.temp_password);
            return;
          }
          Utils.toast(`Decisión registrada: ${r.status}`, 'success');
          if (r.email_skipped) Utils.toast(r.email_skipped, 'warning');
          onCerrar();
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo registrar'),
      },
    );
  }

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !decidir.isPending) onCerrar(); }}
      titulo={d ? `Revisar: ${d.form_name}` : 'Revisar la respuesta'}
      descripcion="Lo diligenciado, lo que cambiaría en el destino y la decisión."
      ancho="lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button variant="destructive" loading={decidir.isPending} onClick={() => decide('rechazar')}>
            <i className="bi bi-x-circle" aria-hidden="true" /> Rechazar
          </Button>
          <Button variant="secondary" loading={decidir.isPending} onClick={() => decide('devolver')}>
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Devolver
          </Button>
          <Button loading={decidir.isPending} onClick={() => decide('aprobar')}>
            <i className="bi bi-check2-circle" aria-hidden="true" /> Aprobar y aplicar
          </Button>
        </>
      }
    >
      {consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : consulta.isError ? (
        <EstadoError err={consulta.error} que="la respuesta" onReintentar={() => void consulta.refetch()} />
      ) : !d ? null : (
        <div className="flex flex-col gap-4">
          <p className="flex flex-wrap items-center gap-2 text-2 text-muted">
            <span><strong className="text-ink">De:</strong> {d.who}</span>
            {d.is_new_person && <Badge tone="info">Nuevo/externo</Badge>}
            <span>· <strong className="text-ink">Enviado:</strong> {fechaHora(d.submitted_at)}</span>
          </p>

          <section>
            <h3 className="mb-1.5 flex items-center gap-2 text-3 font-bold text-brand-ink">
              <i className="bi bi-card-list" aria-hidden="true" /> Información diligenciada
            </h3>
            <dl className="max-h-52 overflow-auto rounded-sm border border-line bg-sunken p-3 text-2">
              {campos.map((x, i) => (
                <div key={i} className="flex justify-between gap-4 py-0.5">
                  <dt className="text-muted">{x.label}</dt>
                  <dd className="min-w-0 break-words text-right font-semibold text-ink">
                    {x.proposed || '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <h3 className="mb-1.5 flex items-center gap-2 text-3 font-bold text-brand-ink">
              <i className="bi bi-arrow-left-right" aria-hidden="true" /> Cambios que se aplicarán
            </h3>
            {cambios.length ? (
              <div className="overflow-auto rounded-sm border border-line">
                <table className="w-full border-collapse text-2">
                  <thead className="bg-sunken">
                    <tr className="border-b border-line text-left text-muted">
                      {['Campo', 'Actual', 'Nuevo'].map((h) => (
                        <th key={h} scope="col" className="px-3 py-2 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cambios.map((x, i) => (
                      <tr key={i} className="border-b border-line last:border-0">
                        <th scope="row" className="px-3 py-1.5 text-left font-semibold text-ink">
                          {x.label}
                        </th>
                        <td className="px-3 py-1.5 text-danger-ink line-through">{x.current || '—'}</td>
                        <td className="px-3 py-1.5 font-semibold text-ok-ink">{x.proposed || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rounded-sm border border-info/40 bg-info/12 p-3 text-2 text-ink-2">
                Sin cambios respecto al destino: lo que llegó ya está guardado así.
              </p>
            )}
          </section>

          <Field
            label="Nota"
            ayuda="Obligatoria al devolver: es lo único que verá quien la recibe."
            error={error || undefined}
          >
            <Textarea
              rows={2}
              value={nota}
              onChange={(e) => { setNota(e.target.value); setError(''); }}
              placeholder="Qué debe corregirse, o el motivo del rechazo"
            />
          </Field>
        </div>
      )}

      <Dialog
        abierto={clave != null}
        onCambio={(v) => { if (!v) { setClave(null); onCerrar(); } }}
        titulo="Usuario creado"
        descripcion="Se creó la cuenta del nuevo colaborador. Esta contraseña temporal también se envió a su correo; no se vuelve a mostrar."
        ancho="max-w-md"
        pie={
          <Button onClick={() => { setClave(null); onCerrar(); }}>Entendido</Button>
        }
      >
        <div className="pb-2">
          <p className="rounded-sm border border-dashed border-line-strong bg-sunken p-3 text-center font-mono text-5 font-bold text-brand-ink">
            {clave}
          </p>
          <p className="mt-2 text-2 text-muted">
            Completa su información administrativa en <strong>Administración → Usuarios</strong> y
            actívalo.
          </p>
        </div>
      </Dialog>
    </Drawer>
  );
}
